import { Button } from '@design/components/button/Button';
import { keyboard } from '@helpers';
import clsx from 'clsx';
import {
  type KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ChipFilteredData } from '../../utils';
import styles from './Chips.module.scss';
import {
  getHorizontalScrollState,
  getHorizontalScrollStep,
  getVerticalWheelScrollDelta,
} from './chipsScrollUtils';

export const CHIPS_WRAPPER_ID = 'chips-wrapper';

export interface ChipsProps {
  chipData: ChipFilteredData[];
  activeChip?: string;
  handleChangeChip: (selectedChip: ChipFilteredData['value']) => void;
  variant?: 'default' | 'secondary';
}

export const Chips = ({
  chipData,
  activeChip,
  handleChangeChip,
  variant = 'default',
}: ChipsProps) => {
  const { t } = useTranslation('chart');
  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement[] | null[]>([]);
  const [showPrevBtn, setShowPrevBtn] = useState(false);
  const [showNextBtn, setShowNextBtn] = useState(false);
  const isSecondaryVariant = variant === 'secondary';

  useLayoutEffect(() => {
    const container = chipsContainerRef.current;

    if (!container) {
      return;
    }

    if (chipData.length === 0) {
      setShowPrevBtn(false);
      setShowNextBtn(false);
      return;
    }

    const { showPrev, showNext } = getHorizontalScrollState(container);
    setShowPrevBtn(showPrev);
    setShowNextBtn(showNext);
  }, [chipData]);

  useEffect(() => {
    const container = chipsContainerRef.current;

    if (!container) {
      return undefined;
    }

    const updateScrollButtons = () => {
      const { showPrev, showNext } = getHorizontalScrollState(container);
      setShowPrevBtn(showPrev);
      setShowNextBtn(showNext);
    };

    const handleWheel = (event: WheelEvent) => {
      const deltaY = getVerticalWheelScrollDelta(
        event,
        getHorizontalScrollState(container)
      );

      if (deltaY === null) {
        return;
      }

      event.preventDefault();
      container.scrollLeft += deltaY;
    };

    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(container);
    container.addEventListener('scroll', updateScrollButtons, {
      passive: true,
    });
    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener('scroll', updateScrollButtons);
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const handleScrollBy = (direction: -1 | 1) => {
    const container = chipsContainerRef.current;

    if (!container) {
      return;
    }

    container.scrollBy({
      left: direction * getHorizontalScrollStep(container.clientWidth),
      behavior: 'smooth',
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const direction =
      e.key === keyboard.left.key
        ? -1
        : e.key === keyboard.right.key
          ? 1
          : null;

    if (direction === null) {
      return;
    }

    const chipsElements = tabsRef.current.filter(Boolean);
    const chipElementIndex = chipsElements.findIndex(c => c === e.target);

    if (chipElementIndex === -1) {
      return;
    }

    const nextIndex =
      (chipElementIndex + direction + chipsElements.length) %
      chipsElements.length;
    chipsElements[nextIndex]?.focus();
  };

  return (
    <div className={styles.wrapper}>
      <div
        ref={chipsContainerRef}
        className={clsx(styles.chipsContainer, {
          [styles.secondary]: isSecondaryVariant,
        })}
        data-testid="chips-wrapper"
        id={CHIPS_WRAPPER_ID}
        onKeyDown={handleKeyDown}
      >
        {chipData.map((chip, i) => (
          <div
            ref={element => {
              tabsRef.current[i] = element;
            }}
            key={chip.name}
            className={clsx(styles.tabButton, {
              [styles.active]: chip.value === activeChip,
              [styles.secondary]: isSecondaryVariant,
            })}
            onClick={() => handleChangeChip(chip.value)}
            data-testid={`chip-${chip.value}`}
            tabIndex={0}
            role="tab"
          >
            {t(chip.name)}
          </div>
        ))}
      </div>
      {showPrevBtn && (
        <Button
          className={clsx(styles.styledButton, styles.prev)}
          iconName="ChevronLightLeft"
          size="small"
          variant="empty"
          onClick={() => handleScrollBy(-1)}
          data-testid="previous-btn"
        />
      )}
      {showNextBtn && (
        <Button
          className={clsx(styles.styledButton, styles.next)}
          iconName="ChevronLightRight"
          size="small"
          variant="empty"
          onClick={() => handleScrollBy(1)}
          data-testid="next-btn"
        />
      )}
    </div>
  );
};
